/* eslint-disable @typescript-eslint/no-non-null-assertion */

import { EntityAdapter, EntityState, createEntityAdapter } from '@ngrx/entity';
import { Action, createReducer, on } from '@ngrx/store';
import { SystemDetails, TIME_RESOLUTION_MAPPING, TimeRangeInterface, TimeResolution } from '@twaice-fe/shared/models';
import { TimeLength, TimeRangeEnum, TimeRangeEnumDE } from '@twaice-fe/shared/utilities';
import { systemActions } from '../actions';

export const SYSTEMS_FEATURE_KEY = 'systems';

interface RemoteState {
  isLoading: boolean;
  error: string | null;
}

export interface State extends RemoteState {
  systemDetails: EntityState<SystemDetails>;
  selectedId?: string;
  timeRange?: TimeRangeInterface;
  availableTimeRanges?: TimeRangeInterface[] | null;
  defaultTimeRange?: TimeRangeEnum | TimeRangeEnumDE;
  isUserSelectedTimeRange?: boolean;
  timeResolution?: TimeResolution | null;
  date?: Date | null;
  isOverridden: boolean;
}

export const systemDetailsAdapter: EntityAdapter<SystemDetails> = createEntityAdapter<SystemDetails>({
  selectId: (systemDetails) => systemDetails.id,
});
export const initialState: State = {
  systemDetails: systemDetailsAdapter.getInitialState(),
  selectedId: '',
  isLoading: false,
  error: null,
  timeRange: undefined,
  availableTimeRanges: null,
  defaultTimeRange: TimeRangeEnum.LAST_THIRTY_DAYS,
  isUserSelectedTimeRange: false,
  timeResolution: (() => {
    const timeRes = Number(new URL(window.location.href).searchParams.get('timeRes'));
    if (timeRes) {
      return TIME_RESOLUTION_MAPPING.find((g) => g.value === timeRes)
        ? TIME_RESOLUTION_MAPPING.find((g) => g.value === timeRes)!
        : TIME_RESOLUTION_MAPPING.find((g) => g.value === TimeLength.QUARTER_HOUR)!;
    }
    return TIME_RESOLUTION_MAPPING.find((g) => g.value === TimeLength.QUARTER_HOUR)!;
  })(),
  date: null,
  isOverridden: false,
};

type ActionReducer<T = unknown> = (state: State, payload: T) => State;

const reduceSystemRouteSettled: ActionReducer<{ systemId?: string }> = (state, { systemId }) => ({
  ...state,
  selectedId: systemId || state.selectedId,
});

const reduceFetchSystemDetails: ActionReducer = (state) => ({
  ...state,
  systemDetails: systemDetailsAdapter.getInitialState(),
  isLoading: true,
  error: null,
});
const reduceLoadSystemDetailsSuccess: ActionReducer<{ systemDetails: SystemDetails[] }> = (state, { systemDetails }) => ({
  ...state,
  systemDetails: systemDetailsAdapter.upsertMany(systemDetails, state.systemDetails),
  isLoading: false,
  error: null,
});
const reduceLoadSystemDetailsFailure: ActionReducer<{ error: string }> = (state, { error }) => ({
  ...state,
  systemDetails: state.systemDetails,
  isLoading: false,
  error,
});

const reduceSetDefaultTimeRange: ActionReducer<{ defaultTimeRange: TimeRangeEnum | TimeRangeEnumDE }> = (
  state,
  { defaultTimeRange }
) => ({
  ...state,
  defaultTimeRange,
  isOverridden: false,
});

const reduceSetTimeRange: ActionReducer<{ timeRange: TimeRangeInterface; isUserSelectedTimeRange?: boolean }> = (
  state,
  { timeRange, isUserSelectedTimeRange }
) => ({
  ...state,
  timeRange,
  isUserSelectedTimeRange: isUserSelectedTimeRange ?? state.isUserSelectedTimeRange,
  isOverridden: false,
});

const reduceSetInitialTimeRange: ActionReducer<{
  timeRange: TimeRangeInterface;
  availableTimeRanges: TimeRangeInterface[];
  isUserSelectedTimeRange?: boolean;
}> = (state, { timeRange, availableTimeRanges, isUserSelectedTimeRange }) => ({
  ...state,
  timeRange: state.isUserSelectedTimeRange ? state.timeRange : timeRange,
  availableTimeRanges,
  isUserSelectedTimeRange: isUserSelectedTimeRange ?? state.isUserSelectedTimeRange,
  isOverridden: true,
});

const reduceSetTimeResolution: ActionReducer<{ granularity: TimeResolution }> = (state, { granularity }) => ({
  ...state,
  timeResolution: granularity,
});

const reduceSetDateValue: ActionReducer<{ date: Date }> = (state, { date }) => ({
  ...state,
  date,
});

const systemsReducer = createReducer(
  initialState,
  on(systemActions.fetchSystemDetails, reduceFetchSystemDetails),
  on(systemActions.loadSystemDetailsSuccess, reduceLoadSystemDetailsSuccess),
  on(systemActions.loadSystemDetailsFailure, reduceLoadSystemDetailsFailure),
  on(systemActions.systemRouteSettled, reduceSystemRouteSettled),
  on(systemActions.setDefaultTimeRange, reduceSetDefaultTimeRange),
  on(systemActions.setTimeRange, reduceSetTimeRange),
  on(systemActions.setInitialTimeRange, reduceSetInitialTimeRange),
  on(systemActions.setTimeResolution, reduceSetTimeResolution),
  on(systemActions.setDefaultDateValue, reduceSetDateValue),
  on(systemActions.setDateValue, reduceSetDateValue)
);

export function reducer(state: State | undefined, action: Action) {
  return systemsReducer(state, action);
}
